const STORAGE_KEY = 'rangeheart.bundle.pending.v1';
const WATCH_KEY = 'rangeheart.bundle.cancelled.v1';
const ATTEMPT_PROPERTY = '_rh_bundle_attempt';
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const bounded = async (promise, ms = 8000) => {
  let timer;
  try { return await Promise.race([promise,new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error('The cart request timed out.')),ms);})]); }
  finally {clearTimeout(timer);}
};

export function aggregateSelections(values, allowed) {
  if (values.length !== 3 || values.some(id => !id || !allowed.includes(String(id)))) throw new Error('Choose all three available caps.');
  const counts = new Map();
  for (const id of values) counts.set(String(id), (counts.get(String(id)) || 0) + 1);
  return [...counts].map(([id, quantity]) => ({ id, quantity }));
}

export function lineIdentity(line) {
  const properties = Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([String(line.variant_id), properties, line.selling_plan_allocation?.selling_plan?.id || null]);
}

export function quantities(items) {
  const result = {};
  for (const line of items) result[lineIdentity(line)] = (result[lineIdentity(line)] || 0) + line.quantity;
  return result;
}

export function cartIdentity(cart) { return String(cart.token || '').split('?')[0]; }

export function patchAttempt(current, id, fields) {
  // A late response can update only its still-pending record, never resurrect a cancelled/replaced one.
  return current?.id === id ? { ...current, ...fields } : current;
}

export function cancellationCanFinish(record, ownedCount) {
  return ownedCount === 0 && (record.addState === 'confirmed' || record.addState === 'rejected' || record.addObserved === true);
}

export function rebindFirstCart(record, cart) {
  if(record.cartId===cartIdentity(cart) || record.cartRebound || Object.keys(record.before).length || !cartIdentity(cart))return record;
  const owned=cart.items.filter(line=>line.properties?.[ATTEMPT_PROPERTY]===record.id);
  const counts={};
  for(const line of owned)counts[String(line.variant_id)]=(counts[String(line.variant_id)]||0)+line.quantity;
  if(owned.reduce((sum,line)=>sum+line.quantity,0)!==3 || record.items.reduce((sum,item)=>sum+item.quantity,0)!==3 || record.items.some(item=>counts[item.id]!==item.quantity))return record;
  // Shopify can replace its provisional empty-cart token on the first add.
  // Only the unique attempt's exact selected three lines prove this first-cart transition.
  return {...record,cartId:cartIdentity(cart),cartRebound:true};
}

export function verifyCart(cart, attempt, { cancelled = false } = {}) {
  if (!cartIdentity(cart) || cartIdentity(cart) !== attempt.cartId) return { ok: false, reason: 'cart-changed' };
  if (cart.currency !== 'USD') return { ok: false, reason: 'currency' };
  const owned = cart.items.filter(line => line.properties?.[ATTEMPT_PROPERTY] === attempt.id);
  const ownedCounts = {};
  for (const line of owned) ownedCounts[String(line.variant_id)] = (ownedCounts[String(line.variant_id)] || 0) + line.quantity;
  if (cancelled) {
    if (owned.length) return { ok: false, reason: 'removal-pending' };
  } else {
    if (owned.reduce((sum, line) => sum + line.quantity, 0) !== 3 || attempt.items.some(item => ownedCounts[item.id] !== item.quantity)) return { ok: false, reason: 'items-pending' };
  }
  const actual = quantities(cart.items.filter(line => line.properties?.[ATTEMPT_PROPERTY] !== attempt.id));
  if (!cancelled && Object.entries(attempt.before).some(([key, quantity]) => (actual[key] || 0) < quantity)) return { ok: false, reason: 'other-items-changed' };
  const eligible = cart.items.filter(line => attempt.allowed.includes(String(line.variant_id)));
  if (eligible.some(line => line.original_price !== 4500 || line.original_line_price !== line.quantity * 4500)) return { ok: false, reason: 'price-changed' };
  const count = eligible.reduce((sum, line) => sum + line.quantity, 0);
  const expectedDiscount = Math.floor(count / 3) * 4500;
  let bogosDiscount = 0;
  for (const line of eligible) {
    for (const allocation of line.line_level_discount_allocations || []) {
      const application = allocation.discount_application;
      if (/^BOGOS-/.test(application?.title || '') && application.type === 'discount_code' && application.target_type === 'line_item' && application.value_type === 'percentage' && Number(application.value) === 100) bogosDiscount += allocation.amount;
    }
  }
  if (bogosDiscount !== expectedDiscount) return { ok: false, reason: 'discount-pending' };
  return { ok: true, count, bogosDiscount };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const base = window.Shopify?.routes?.root || '/';
  let busy = false;
  let running = false;
  let dialog;
  let dialogMode = 'pending';
  let requestInFlight = false;

  const load = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; } };
  const save = record => localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  const patch = (id, fields) => { const current=load(); const next=patchAttempt(current,id,fields); if(next && next!==current)save(next); return next; };
  const clear = id => { if (load()?.id === id) localStorage.removeItem(STORAGE_KEY); };
  const watches = () => {try{return JSON.parse(localStorage.getItem(WATCH_KEY)||'{}');}catch{return {};}};
  const unwatch = id => {const all=watches();delete all[id];localStorage.setItem(WATCH_KEY,JSON.stringify(all));};
  const watch = record => {localStorage.setItem(WATCH_KEY,JSON.stringify({...watches(),[record.id]:{...record,cancelRequested:true}}));};
  const getCart = () => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('GET', base + 'cart.js?rh_snapshot=' + Date.now());
    request.timeout = 8000;
    request.setRequestHeader('Cache-Control', 'no-cache');
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) { reject(new Error('Could not check the cart.')); return; }
      try { resolve(JSON.parse(request.responseText)); } catch { reject(new Error('Could not read the cart.')); }
    };
    request.onerror = () => reject(new Error('Could not check the cart.'));
    request.ontimeout = () => reject(new Error('The cart request timed out.'));
    request.send();
  });

  function ensureDialog() {
    if (dialog?.isConnected) return;
    dialog = document.createElement('dialog');
    dialog.className = 'rh-bundle-dialog';
    dialog.setAttribute('aria-labelledby', 'rh-bundle-status-title');
    dialog.innerHTML = '<h2 id="rh-bundle-status-title">Checking your three caps</h2><p data-status role="status" aria-live="polite"></p><button type="button" data-recheck>Check again</button><button type="button" data-cancel>Remove these caps and cancel</button>';
    dialog.addEventListener('cancel', event => event.preventDefault());
    dialog.querySelector('[data-recheck]').addEventListener('click', async () => {
      const record = load();
      if (dialogMode === 'success' && record) {
        try {
          const cart = await getCart();
          const latest=load();
          if(latest?.id!==record.id){status('Your cart changed. Please review it before continuing.','closed');return;}
          if(latest.cancelRequested){await settle(latest,true);return;}
          if(await removeWatchedLines(cart)){await settle(latest);return;}
          if (!verifyCart(cart, record).ok) { status('Your cart changed. Checking the offer again.'); await settle(record); return; }
          const ready=load();
          if(ready?.id!==record.id){status('Your cart changed. Please review it before continuing.','closed');return;}
          if(ready.cancelRequested){await settle(ready,true);return;}
          clear(record.id);
          dialog.close();
          window.location.assign(base + 'cart');
        } catch { status('Could not confirm the cart. Check again before continuing.'); }
      } else if (dialogMode === 'closed') { dialog.close(); }
      else if (record && !running) await settle(record);
    });
    dialog.querySelector('[data-cancel]').addEventListener('click', cancelAttempt);
    document.body.appendChild(dialog);
  }

  function status(message, mode = 'pending') {
    ensureDialog();
    dialogMode = mode;
    dialog.querySelector('[data-status]').textContent = message;
    dialog.querySelector('h2').textContent = mode === 'success' ? 'Your offer is applied' : mode === 'closed' ? 'Cart checked' : 'Checking your three caps';
    const recheck = dialog.querySelector('[data-recheck]');
    recheck.textContent = mode === 'success' ? 'Review cart' : mode === 'closed' ? 'Continue shopping' : 'Check again';
    recheck.disabled = running;
    const cancel = dialog.querySelector('[data-cancel]');
    cancel.hidden = mode === 'closed';
    cancel.disabled = running;
    if (!dialog.open) dialog.showModal();
  }

  async function notifyCart(cart) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'rangeheart-cap-bundle', didError: false } }) }));
    } catch { /* The verified cart remains accessible through its ordinary URL. */ }
  }

  async function removeWatchedLines(cart) {
    let changed=false;
    for(const [id,saved] of Object.entries(watches())) {
      const record=rebindFirstCart(saved,cart);
      if(record!==saved)watch(record);
      if(record.cartId!==cartIdentity(cart)){
        if(record.cartRebound || Object.keys(record.before).length)unwatch(id);
        continue;
      }
      const line=cart.items.find(item=>item.properties?.[ATTEMPT_PROPERTY]===id);
      if(!line)continue;
      const response=await bounded(fetch(base+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:line.key,quantity:0})}));
      if(!response.ok)throw new Error('Could not remove a cancelled selection.');
      changed=true;
    }
    return changed;
  }

  async function recoverWatched() {
    if(load() || running || requestInFlight || !Object.keys(watches()).length)return;
    try {
      const cart=await getCart();
      for(const [id,saved] of Object.entries(watches())) {
        const record=rebindFirstCart(saved,cart);
        if(record!==saved)watch(record);
        if(record.cartId!==cartIdentity(cart)){
          if(record.cartRebound || Object.keys(record.before).length)unwatch(id);
          continue;
        }
        if(!cart.items.some(line=>line.properties?.[ATTEMPT_PROPERTY]===id))continue;
        if(load()||running)return;
        save({...record,addObserved:true,cancelRequested:true,stage:'checking'});
        unwatch(id);
        await settle(load(),true);
        return;
      }
    } catch { /* Keep cancellation intent for the next cart/page check; no success claim. */ }
  }

  async function settle(record, cancelled = false) {
    if (running) return;
    running = true;
    status(cancelled ? 'Removing your selected caps…' : 'Adding your caps…');
    let previous = '';
    let lastCart;
    let lastResult;
    let emptyCancelledReads=0;
    try {
      for (let check = 0; check < 24; check++) {
        if (load()?.id !== record.id) return;
        record = load();
        cancelled = cancelled || record.cancelRequested === true;
        lastCart = await getCart();
        if(load()?.id!==record.id)return;
        record=load();
        const rebound=rebindFirstCart(record,lastCart);
        if(rebound!==record)record=patch(record.id,{cartId:rebound.cartId,cartRebound:true});
        cancelled=cancelled || record.cancelRequested===true;
        if(await removeWatchedLines(lastCart)){previous='';continue;}
        const own = lastCart.items.filter(line=>line.properties?.[ATTEMPT_PROPERTY]===record.id);
        const provisionalEmptyBaseline=!record.cartRebound && Object.keys(record.before).length===0 && record.cartId!==cartIdentity(lastCart) && own.length===0;
        if(own.length && !record.addObserved) record=patch(record.id,{addObserved:true});
        if(cancelled && (cartIdentity(lastCart)===record.cartId || provisionalEmptyBaseline)) {
          if(own.length) {
            const response=await bounded(fetch(base+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:own[0].key,quantity:0})}));
            if(!response.ok)throw new Error('Removal failed');
            previous='';
            await sleep(300);
            continue;
          }
          if(!cancellationCanFinish(record,0) || provisionalEmptyBaseline) {
            // Release an absent uncertain add without losing its late-arrival cancellation intent.
            // This is a read-only check of the currently visible remainder, not permission to rebind or remove its lines.
            const remainingRecord=provisionalEmptyBaseline?{...record,cartId:cartIdentity(lastCart)}:record;
            const remainingVerified=verifyCart(lastCart,remainingRecord,{cancelled:true}).ok;
            const remainingSignature=JSON.stringify([lastCart.total_price,lastCart.items.map(line=>[line.key,line.quantity,line.final_line_price,line.line_level_discount_allocations])]);
            emptyCancelledReads=remainingVerified ? (remainingSignature===previous ? emptyCancelledReads+1 : 1) : 0;
            previous=remainingVerified?remainingSignature:'';
            if(emptyCancelledReads>=2) {
              watch(record);
              clear(record.id);
              running=false;
              status('No selected caps are currently in your cart. You can continue shopping; if a delayed addition arrives, we’ll remove those caps.','closed');
              return;
            }
            await sleep(500);
            continue;
          }
        }
        lastResult = provisionalEmptyBaseline ? {ok:false,reason:'items-pending'} : verifyCart(lastCart, record, { cancelled });
        if (lastResult.reason === 'cart-changed') {
          clear(record.id);
          running = false;
          status('This is a different cart. No items were removed or added during recovery. Please review your current cart.', 'closed');
          return;
        }
        const signature = JSON.stringify([lastCart.total_price, lastCart.items.map(line => [line.key,line.quantity,line.final_line_price,line.line_level_discount_allocations])]);
        if (lastResult.ok && signature === previous) {
          const latest=load();
          if(latest?.id!==record.id)return;
          if(latest.cancelRequested && !cancelled){cancelled=true;previous='';continue;}
          running = false;
          if (cancelled) {
            clear(record.id);
            status('This selection was removed. The remaining cart and its offer have been checked.', 'closed');
          } else {
            const verified=patch(record.id,{stage:'verified'});
            if(verified?.id!==record.id)return;
            if(verified.cancelRequested){running=true;cancelled=true;previous='';continue;}
            status('Your three caps are in the cart and the offer is applied. Review your total before checkout; other items and taxes are additional.', 'success');
            await notifyCart(lastCart);
          }
          return;
        }
        previous = lastResult.ok ? signature : '';
        await sleep(500);
      }
      running = false;
      status(cancelled ? 'We’re still confirming the removal and your remaining cart total. Please check again.' : lastResult?.reason === 'other-items-changed' ? 'Your cart changed. Check again, or remove these caps and review the remaining cart.' : 'We couldn’t apply the three-cap offer yet. Check again or remove these caps.');
    } catch {
      running = false;
      status('We could not confirm the cart. Your selection may already be added. Check again or remove this selection; do not add it a second time.');
    } finally { running = false; if (dialog?.open) {dialog.querySelector('[data-recheck]').disabled = false; dialog.querySelector('[data-cancel]').disabled = false;} }
  }

  async function cancelAttempt() {
    if (running) return;
    const record = load();
    if (!record) { status('There is no pending selection.', 'closed'); return; }
    patch(record.id,{cancelRequested:true});
    await settle(load(), true);
  }

  class CapBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      if (!window.HTMLDialogElement || !window.crypto?.randomUUID) return;
      try { localStorage.setItem(STORAGE_KEY + '.test','1'); localStorage.removeItem(STORAGE_KEY + '.test'); } catch { return; }
      this.native = this.closest('.shopify-section')?.querySelector('.buy-buttons-block');
      if (!this.native) return;
      this.querySelector('[data-bundle-fallback]').hidden = true;
      this.querySelector('[data-bundle-enhanced]').hidden = false;
      this.dataset.enhanced = 'true';
      this.querySelector('[data-choose]').addEventListener('click', () => {
        this.querySelector('input[value="bundle"]').checked = true;
        this.setMode();
        this.querySelector('[data-cap-choice]').focus();
      });
      this.querySelectorAll('input[type="radio"]').forEach(input => input.addEventListener('change', () => this.setMode()));
      this.querySelectorAll('[data-cap-choice]').forEach(select => select.addEventListener('change', () => this.validate()));
      this.querySelector('[data-bundle-add]').addEventListener('click', () => this.add());
      this.setMode();
    }
    disconnectedCallback() { if(this.native){this.native.hidden=false;this.native.inert=false;} }
    setMode() {
      const bundle = this.querySelector('input[value="bundle"]').checked;
      this.querySelector('[data-builder]').hidden = !bundle;
      this.querySelector('[data-choose]').hidden = bundle;
      this.native.hidden = bundle;
      this.native.inert = bundle;
      this.validate();
    }
    validate() {
      const valid = [...this.querySelectorAll('[data-cap-choice]')].every(select=>select.value && !select.selectedOptions[0].disabled);
      this.querySelector('[data-bundle-add]').disabled = !valid || busy;
      this.querySelector('[data-bundle-message]').textContent = valid ? 'Your three are ready.' : 'Choose all three caps to continue.';
      for(const select of this.querySelectorAll('[data-cap-choice]')) {
        const image=select.parentElement.querySelector('[data-cap-image]');
        const option=select.selectedOptions[0];
        image.hidden=!option?.dataset.image;
        if(option?.dataset.image){image.src=option.dataset.image;image.alt=option.textContent;}
        else image.removeAttribute('src');
      }
      return valid;
    }
    async add() {
      if (busy || !this.validate()) return;
      if (load()) { await settle(load()); return; }
      busy = true;
      this.validate();
      let record;
      try {
        const selects = [...this.querySelectorAll('[data-cap-choice]')];
        const allowed = [...selects[0].options].filter(option => option.value).map(option => option.value);
        const items = aggregateSelections(selects.map(select=>select.value),allowed);
        // Fresh product JSON validates current availability and USD price; Shopify enforces stock again on add.
        for (const item of items) {
          const option = [...selects[0].options].find(option=>option.value===item.id);
          const response = await bounded(fetch(base+'products/'+encodeURIComponent(option.dataset.handle)+'.js',{cache:'no-store'}));
          if (!response.ok) throw new Error('Could not check a selected cap. Please try again.');
          const product = await bounded(response.json());
          const variant = product.variants.find(variant=>String(variant.id)===item.id);
          if (!variant?.available || variant.price!==4500) throw new Error('A selected cap is unavailable or its price changed. Please refresh before choosing again.');
        }
        let cart = await getCart();
        for(let i=0;i<12 && await removeWatchedLines(cart);i++)cart=await getCart();
        if(cart.items.some(line=>watches()[line.properties?.[ATTEMPT_PROPERTY]]))throw new Error('A cancelled selection is still being removed. Please check your cart before adding more.');
        if (cart.currency!=='USD') throw new Error('This offer is shown in USD. Please use the individual cap purchase flow for your current currency. No caps were added.');
        if (load()) { await settle(load()); return; }
        record={id:crypto.randomUUID(),cartId:cartIdentity(cart),created:Date.now(),items,allowed,before:quantities(cart.items),stage:'adding',addState:'pending',cancelRequested:false};
        save(record);
        await sleep(30);
        if(load()?.id!==record.id) throw new Error('Another cart action started. Please review the cart before trying again.');
        requestInFlight=true;
        status('Adding your caps…');
        // Use page fetch: the installed BOGOS wrapper and observers perform the real discount processing.
        let response;
        try {
          const addRequest=fetch(base+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:items.map(item=>({...item,properties:{[ATTEMPT_PROPERTY]:record.id}}))})});
          addRequest.then(result=>{
            const current=patch(record.id,{addState:result.ok?'confirmed':'rejected'});
            if(!requestInFlight && !running && current?.id===record.id)settle(current);
          },()=>{patch(record.id,{addState:'uncertain'});});
          response=await Promise.race([addRequest,sleep(15000).then(()=>null)]);
        } finally {requestInFlight=false;}
        if(!response) {
          patch(record.id,{stage:'checking'});
          if(load()?.id===record.id)await settle(load());
          return;
        }
        if(load()?.id!==record.id)return;
        if(!response.ok) {
          const error=await response.json().catch(()=>({}));
          const current=await getCart();
          if(!current.items.some(line=>line.properties?.[ATTEMPT_PROPERTY]===record.id)) {
            clear(record.id);
            status(error.description || 'The selected quantity could not be added. No bundle was added; please update your selection.', 'closed');
            return;
          }
        }
        patch(record.id,{stage:'checking'});
        if(load()?.id===record.id)await settle(load());
      } catch(error) {
        requestInFlight=false;
        if(record && load()?.id===record.id) await settle(record);
        else this.querySelector('[data-bundle-message]').textContent=error.message || 'Could not add caps. Please check your cart.';
      } finally {busy=false;requestInFlight=false;this.validate();}
    }
  }
  if(!customElements.get('rh-cap-bundle'))customElements.define('rh-cap-bundle',CapBundle);
  // Local storage is shared across tabs; an attempt never removes lines belonging to another cart or attempt.
  window.addEventListener('storage',event=>{if(event.key===STORAGE_KEY && load() && !running && !requestInFlight)settle(load());});
  window.addEventListener('pageshow',()=>{if(load() && !running && !requestInFlight)settle(load());});
  window.addEventListener('pageshow',recoverWatched);
  document.addEventListener('bogos:cart-updated',recoverWatched);
  setInterval(recoverWatched,5000);
  if(load())settle(load());
  else recoverWatched();
}
